import { supabase } from '../supabaseClient';

export const commissionRecordsService = {
    async getAll(agencyId = null) {
        let query = supabase
            .from('commission_records')
            .select('*');

        // Si se proporciona agencyId, filtrar por él
        if (agencyId) {
            query = query.eq('agency_id', agencyId);
        }

        query = query.order('created_at', { ascending: false });

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    },

    async getByUploadId(uploadId) {
        // Obtener registros por upload_id
        const { data, error } = await supabase
            .from('commission_records')
            .select('*')
            .eq('upload_id', uploadId);

        if (error) throw error;
        return data || [];
    },

    // Get records for a specific agent by NPN
    // IMPORTANTE: Prioriza agent_user_id si está disponible, luego NPN
    async getByAgentNpn(npn, agentUserId = null) {
        let query = supabase
            .from('commission_records')
            .select('*');

        // Si tenemos agent_user_id, usarlo primero (más preciso)
        // NOTA: commissions.user_id es el PUBLICADOR (agencia), no el agente.
        // Así que confiamos en writing_agent_npn

        if (npn) {
            query = query.eq('writing_agent_npn', npn);
        } else {
            return [];
        }

        // Ordenar por statement_date si existe, sino por created_at
        query = query.order('statement_date', { ascending: false, nullsLast: true })
            .order('created_at', { ascending: false });

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    },

    // Get agent stats for current month
    // IMPORTANTE: Usa statement_date en lugar de created_at para coincidir con datos publicados
    async getAgentMonthlyStats(npn) {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        // Crear rango del mes actual para statement_date
        const startDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        const endDate = new Date(currentYear, currentMonth, 0).toISOString().split('T')[0];

        // Intentar primero con statement_date (datos publicados)
        let query = supabase
            .from('commission_records')
            .select('split_agent, policy_number, statement_date, created_at')
            .eq('writing_agent_npn', npn)
            .gte('statement_date', startDate)
            .lte('statement_date', endDate);

        const { data: dataByStatement, error: errorByStatement } = await query;

        // Si no hay datos por statement_date, fallback a created_at (datos antiguos)
        if (!errorByStatement && dataByStatement && dataByStatement.length > 0) {
            const stats = {
                monthlyCommissions: 0,
                activePolicies: new Set(),
                totalMembers: 0,
                records: dataByStatement || []
            };

            dataByStatement.forEach(record => {
                // Usar split_agent para lo que recibe el agente
                stats.monthlyCommissions += parseFloat(record.split_agent) || 0;
                if (record.policy_number) stats.activePolicies.add(record.policy_number);
            });

            stats.activePoliciesCount = stats.activePolicies.size;
            return stats;
        }

        // Retornamos vacío si no hay datos nuevos (asumimos que todo migrará a la nueva tabla)
        return {
            monthlyCommissions: 0,
            activePoliciesCount: 0,
            totalMembers: 0,
            records: []
        };
    },

    // Obtener total de comisiones (todas las comisiones del agente)
    async getTotalCommissions(npn) {
        const { data, error } = await supabase
            .from('commission_records')
            .select('split_agent')
            .eq('writing_agent_npn', npn);

        if (error) throw error;

        const total = (data || []).reduce((sum, record) => {
            return sum + (parseFloat(record.split_agent) || 0);
        }, 0);

        return total;
    },

    // Obtener distribución de comisiones por carrier
    async getCommissionsByCarrier(npn) {
        const { data, error } = await supabase
            .from('commission_records')
            .select('carrier, split_agent')
            .eq('writing_agent_npn', npn);

        if (error) throw error;

        const distribution = {};
        (data || []).forEach(record => {
            const carrier = record.carrier || 'Sin Carrier';
            const amount = parseFloat(record.split_agent) || 0;
            distribution[carrier] = (distribution[carrier] || 0) + amount;
        });

        return distribution;
    },

    // Get unassigned commissions (NPNs not in profiles)
    async getUnassignedCommissions() {
        // First get all unique NPNs from commission_records
        const { data: records, error: recordsError } = await supabase
            .from('commission_records')
            .select('writing_agent_npn, split_agent, carrier, product');

        if (recordsError) throw recordsError;

        // Get all registered NPNs from profiles
        const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('npn');

        if (profilesError) throw profilesError;

        const registeredNpns = new Set((profiles || []).map(p => p.npn).filter(Boolean));

        // Group unassigned commissions by NPN
        const unassignedMap = {};

        (records || []).forEach(record => {
            const npn = record.writing_agent_npn;
            if (!npn || registeredNpns.has(npn)) return;

            if (!unassignedMap[npn]) {
                unassignedMap[npn] = {
                    npn,
                    totalAmount: 0,
                    recordCount: 0,
                    carriers: new Set()
                };
            }

            unassignedMap[npn].totalAmount += parseFloat(record.split_agent) || 0;
            unassignedMap[npn].recordCount += 1;
            if (record.carrier) unassignedMap[npn].carriers.add(record.carrier);
        });

        // Convert to array and format
        return Object.values(unassignedMap).map(item => ({
            ...item,
            carriers: Array.from(item.carriers).join(', ')
        })).sort((a, b) => b.totalAmount - a.totalAmount);
    },

    async insertBatch(records, onProgress) {
        // Validar que todos los registros tengan agency_id
        const recordsWithoutAgencyId = records.filter(r => !r.agency_id);
        if (recordsWithoutAgencyId.length > 0) {
            throw new Error(`Faltan ${recordsWithoutAgencyId.length} registros sin agency_id`);
        }

        // Validar que todos los registros tengan policy_id, carrier y agency_id para UPSERT
        const recordsWithoutRequiredFields = records.filter(r => !r.policy_id || !r.carrier || !r.agency_id);
        if (recordsWithoutRequiredFields.length > 0) {
        }

        const results = { success: 0, updated: 0, errors: [] };
        const batchSize = 100;

        for (let i = 0; i < records.length; i += batchSize) {
            const batch = records.slice(i, i + batchSize);

            // Separar registros con y sin policy_id/carrier/agency_id
            const recordsWithKey = batch.filter(r => r.policy_id && r.carrier && r.agency_id);
            const recordsWithoutKey = batch.filter(r => !r.policy_id || !r.carrier || !r.agency_id);

            // UPSERT para registros con policy_id, carrier y agency_id (usando la restricción UNIQUE)
            // IMPORTANTE: ignoreDuplicates: true para NO sobrescribir registros existentes
            if (recordsWithKey.length > 0) {
                const { data, error } = await supabase
                    .from('commission_records')
                    .upsert(recordsWithKey, {
                        onConflict: 'policy_id,carrier,agency_id',
                        ignoreDuplicates: true
                    })
                    .select();

                if (error) {
                    // Si falla con el formato de columnas, intentar con el nombre de la constraint
                    if (error.code === '42P10' || error.message?.includes('ON CONFLICT')) {
                        const { data: retryData, error: retryError } = await supabase
                            .from('commission_records')
                            .upsert(recordsWithKey, {
                                onConflict: 'commission_records_policy_carrier_agency_unique',
                                ignoreDuplicates: true
                            })
                            .select();

                        if (retryError) {
                            results.errors.push({
                                batch: Math.floor(i / batchSize),
                                error: retryError.message,
                                records: recordsWithKey.length
                            });
                        } else {
                            // Si ignoreDuplicates: true, data contiene SOLO los nuevos registros insertados.
                            results.success += retryData?.length || 0;
                        }
                    } else {
                        results.errors.push({
                            batch: Math.floor(i / batchSize),
                            error: error.message,
                            records: recordsWithKey.length
                        });
                    }
                } else {
                    // Si ignoreDuplicates: true, data contiene SOLO los nuevos registros insertados.
                    results.success += data?.length || 0;
                }
            }

            // INSERT normal para registros sin policy_id/carrier/agency_id (no pueden usar UPSERT)
            if (recordsWithoutKey.length > 0) {
                const { error } = await supabase
                    .from('commission_records')
                    .insert(recordsWithoutKey);

                if (error) {
                    results.errors.push({
                        batch: Math.floor(i / batchSize),
                        error: error.message,
                        records: recordsWithoutKey.length
                    });
                } else {
                    results.success += recordsWithoutKey.length;
                }
            }

            if (onProgress) {
                const progress = Math.min(100, Math.round(((i + batch.length) / records.length) * 100));
                onProgress(progress);
            }
        }

        return results;
    },

    async getStats() {
        const { data, error } = await supabase
            .from('commission_records')
            .select('commission_amount, split_agent, split_agency');
        if (error) throw error;

        const stats = {
            totalReceived: 0,
            totalAgentPayout: 0,
            totalAgencyProfit: 0,
            recordCount: data?.length || 0
        };

        (data || []).forEach(record => {
            stats.totalReceived += parseFloat(record.commission_amount) || 0;
            stats.totalAgentPayout += parseFloat(record.split_agent) || 0;
            stats.totalAgencyProfit += parseFloat(record.split_agency) || 0;
        });

        return stats;
    }
};
